import {
  useState,
  useCallback,
  useEffect,
} from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';
import { goalsService } from '@/services/goals.service';
import { LoadingScreen } from '@/components/ui/loading-screen';
import { TOTAL_CARDS } from './constants';
import { formatDate } from './utils';
import { CardCelebration } from './CardCelebration';
import { CardGoalSummary } from './CardGoalSummary';
import { CardReadingDays } from './CardReadingDays';
import { CardStreak } from './CardStreak';
import { CardDailyChart } from './CardDailyChart';
import { CardClosing } from './CardClosing';

export function GoalCompletionStory() {
  const { goalId } = useParams<{ goalId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isWrapUp = searchParams.get('partial') === '1';

  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());
    api.on('select', () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const { data: summary, isLoading, error } = useQuery({
    queryKey: ['goals', 'summary', goalId],
    queryFn: () => goalsService.getGoalSummary(goalId ?? ''),
    enabled: !!goalId,
  });

  const handleClose = useCallback(() => navigate(-1), [navigate]);

  /* ── Guards ── */
  if (!goalId) {
    navigate('/dashboard', { replace: true });
    return null;
  }
  if (isLoading) return <LoadingScreen />;
  if (error || !summary) {
    return (
      <div
        className="flex min-h-dvh flex-col items-center justify-center bg-background px-5 py-8 sm:px-8"
        style={{
          paddingTop: 'max(2rem, env(safe-area-inset-top))',
          paddingBottom: 'max(2rem, env(safe-area-inset-bottom))',
        }}
      >
        <p className="mb-4 text-destructive">โหลดสรุปเป้าหมายไม่สำเร็จ</p>
        <Button onClick={() => navigate('/dashboard')}>กลับหน้าหลัก</Button>
      </div>
    );
  }

  const chartData = summary.daily_pages.map((d) => ({
    date: formatDate(d.date),
    pages: d.pages_read,
  }));

  const cards = [
    <CardCelebration key="celebration" isWrapUp={isWrapUp} className="min-h-0 flex-1" />,
    <CardGoalSummary key="summary" summary={summary} active={current >= 1} className="min-h-0 flex-1" />,
    <CardReadingDays key="reading" summary={summary} active={current >= 2} className="min-h-0 flex-1" />,
    <CardStreak key="streak" summary={summary} active={current >= 3} className="min-h-0 flex-1" />,
    <CardDailyChart key="chart" summary={summary} chartData={chartData} className="min-h-0 flex-1" />,
    <CardClosing key="closing" summary={summary} active={current >= 5} className="min-h-0 flex-1" />,
  ];

  /* ───── Render ───── */

  return (
    <div
      className="relative flex h-dvh max-h-dvh min-h-dvh items-center justify-center overflow-hidden bg-muted/40"
    >
      {/* ── Slider Container ── */}
      <Carousel
        setApi={setApi}
        opts={{
          align: 'start',
          loop: false,
        }}
        className="relative h-full w-full max-w-md touch-none select-none"
      >
        <CarouselContent className="h-full ml-0">
          {cards.map((card, i) => (
            <CarouselItem
              key={i}
              className="pl-0 relative flex h-full min-w-full shrink-0 flex-col items-center justify-center p-4 sm:p-6"
            >
              <div className="flex h-full w-full flex-col overflow-hidden rounded-[2rem] bg-background shadow-xl ring-1 ring-border/20">
                {card}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {/* ── Floating Close Button (Top Right) ── */}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={handleClose}
        className="absolute right-4 top-[max(1rem,env(safe-area-inset-top))] z-[100] h-10 w-10 rounded-full bg-background/30 text-foreground shadow-sm backdrop-blur-md hover:bg-background/50"
        aria-label="ปิด"
      >
        <span className="sr-only">ปิด</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 6 6 18" />
          <path d="m6 6 12 12" />
        </svg>
      </Button>

      {/* Pagination indicators */}
      <div className="absolute inset-x-0 bottom-6 z-10 flex justify-center gap-1.5 sm:bottom-8 pointer-events-none">
        {Array.from({ length: TOTAL_CARDS }).map((_, i) => (
          <div
            key={`dot-${i}`}
            className={`transition-all duration-300 ${
              i === current
                ? 'h-2 w-6 rounded-full bg-primary'
                : 'h-2 w-2 rounded-full bg-primary/20'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default GoalCompletionStory;
